import { CSSObject } from "@emotion/styled";

export interface PrimaryFonts {
  [name: string]: CSSObject;
}

const primaryFontFamily =
  'var(--font-inter), -apple-system, Helvetica, Arial, BlinkMacSystemFont,"Segoe UI", Roboto, sans-serif;';

// Variable Font
export const FontWeight = {
  BLACK: '"wght" 600',
  BOLD: '"wght" 600',
  MEDIUM: '"wght" 500',
  BOOK: '"wght" 400',
  LIGHT: '"wght" 400',
};

export type FontWeightOpts = keyof typeof FontWeight;

export const BodyVariants = {
  XL_MED: "body-xl-medium",
  XL_BOLD: "body-xl-bold",
  XL_BOOK: "body-xl",
  XL_LIGHT: "body-xl-light",
  XL_LESS_BOOK: "body-xl-less",
  XL_LESS: "body-xl-less-medium",
  XL_LESS_LIGHT: "body-xl-less-light",
  XL_LESS_LIGHT_SMALL_LINE_HEIGHT: "body-xl-less-light-small-line-height",
  LRG_MED: "body-l-medium",
  LRG_BOOK: "body-l",
  LRG_LIGHT: "body-l-light",
  MED_MED: "body-m-medium",
  MED_BOOK: "body-m",
  MED_LIGHT: "body-m-light",
  SML_MED: "body-s-medium",
  SML_BOLD: "body-s-bold",
  SML_BOOK: "body-s",
  SML_LIGHT: "body-s-light",
  MICRO: "body-micro",
  // We in our text components reset the book font to 325 (per figma) when the modifier includes book. Allowing this as an optin to reset to 400
  MICRO_BOOK: "body-micro",
  MICRO_LIGHT: "body-micro-light",
} as const;

export type BodyVariantOpts = keyof typeof BodyVariants;

export const primaryFonts: PrimaryFonts = {
  h1: {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 64,
    lineHeight: "69px",
  },
  h2: {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 48,
    lineHeight: "52px",
  },
  "h2-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 48,
    lineHeight: "52px",
  },
  h3: {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 32,
    lineHeight: "42px",
  },
  h4: {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 24,
    lineHeight: "35px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  "h4-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 24,
    lineHeight: "35px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  h5: {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 12,
    lineHeight: "17px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  "h5-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 12,
    lineHeight: "17px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  h6: {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 10,
    lineHeight: "14px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  h7: {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 10,
    lineHeight: "14px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  "body-xl-bold": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 22,
    lineHeight: "32px",
  },
  "body-xl-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 22,
    lineHeight: "32px",
  },
  "body-xl": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 22,
    lineHeight: "32px",
  },
  "body-xl-light": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 22,
    lineHeight: "32px",
  },
  "body-xl-less-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 18,
    lineHeight: "37px",
  },
  "body-xl-less": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 18,
    lineHeight: "37px",
  },
  "body-xl-less-light": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 18,
    lineHeight: "37px",
  },
  "body-xl-less-light-small-line-height": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 18,
    lineHeight: "24px",
  },
  "body-l-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 16,
    lineHeight: "27px",
  },
  "body-l": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 16,
    lineHeight: "27px",
  },
  "body-l-light": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 16,
    lineHeight: "27px",
  },
  "body-m-bold": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 14,
    lineHeight: "20px",
  },
  "body-m-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 14,
    lineHeight: "20px",
  },
  "body-m": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 14,
    lineHeight: "24px",
  },
  "body-m-light": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 14,
    lineHeight: "24px",
  },
  "body-s-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 12,
    lineHeight: "20px",
  },
  "body-s": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 12,
    lineHeight: "20px",
  },
  "body-s-bold": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 12,
    lineHeight: "20px",
  },
  "body-s-light": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 12,
    lineHeight: "20px",
  },
  "body-micro": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 10,
    lineHeight: "16px",
  },
  "body-micro-light": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 10,
    lineHeight: "16px",
  },
  "button-xl-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 16,
    lineHeight: "23px",
  },
  "button-xl": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 14,
    lineHeight: "20px",
  },
  "button-xl-light": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 14,
    lineHeight: "20px",
  },
  "button-l": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 12,
    lineHeight: "17px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  "button-l-light": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.LIGHT,
    fontSize: 12,
    lineHeight: "17px",
    letterSpacing: "0.05em",
  },
  "button-m": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 11,
    lineHeight: "16px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  "button-s": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOLD,
    fontSize: 12,
    lineHeight: "17px",
  },
  "button-s-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 12,
    lineHeight: "14px",
  },
  "form-l": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 14,
    lineHeight: "17px",
  },
  "form-m-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 12,
    lineHeight: "14px",
  },
  "form-m-medium-c": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 12,
    lineHeight: "14px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  "form-m": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.BOOK,
    fontSize: 12,
    lineHeight: "14px",
  },
  "form-s-medium": {
    fontFamily: primaryFontFamily,
    fontVariationSettings: FontWeight.MEDIUM,
    fontSize: 9,
    lineHeight: "11px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
};
